import { createContext, FunctionComponent, ReactNode, useContext, useMemo } from 'react';
import { useCookies } from 'react-cookie';
import { getEnvironmentDomain } from '../utils/getEnvironmentDomain';
import { Me, useMe } from './useMe';

type AuthState =
	| {
			readonly isLoading: true;
	  }
	| {
			readonly isLoading: false;
			readonly isSignedIn: false;
	  }
	| {
			readonly isLoading: false;
			readonly isSignedIn: true;
			readonly token: string;
			readonly me: Me;
	  };

type AuthValue = {
	readonly state: AuthState;
	readonly setSignedIn: (token: string) => void;
	readonly setSignedOut: () => void;
};

type AuthProviderProps = {
	readonly children: ReactNode;
};

const defaultAuthState: AuthState = {
	isLoading: true,
};

const defaultAuthValue: AuthValue = {
	state: defaultAuthState,
	setSignedIn: () => {},
	setSignedOut: () => {},
};

const AuthContext = createContext<AuthValue>(defaultAuthValue);

const useAuth = (): AuthState => {
	const { state } = useContext(AuthContext);

	return state;
};

const AuthProvider: FunctionComponent<AuthProviderProps> = ({ children }) => {
	const [{ token, ood_impersonated_token }, setToken, removeToken] = useCookies(['token', 'ood_impersonated_token']);
	const { meData, isMeLoading } = useMe();

	const state: AuthState = useMemo(() => {
		if (isMeLoading) {
			return { isLoading: true };
		}

		if (ood_impersonated_token != null && meData != null) {
			return { isLoading: false, isSignedIn: true, token: ood_impersonated_token, me: meData.me };
		}
		if (token != null && meData != null) {
			return { isLoading: false, isSignedIn: true, token: token, me: meData.me };
		}

		return { isLoading: false, isSignedIn: false };
	}, [isMeLoading, meData, ood_impersonated_token, token]);

	return (
		<AuthContext.Provider
			value={{
				state,
				setSignedIn: (token) => {
					setToken('token', token, { domain: getEnvironmentDomain() });
				},
				setSignedOut: () => {
					removeToken('token', { domain: getEnvironmentDomain() });
					removeToken('ood_impersonated_token', { domain: getEnvironmentDomain() });
				},
			}}
		>
			{children}
		</AuthContext.Provider>
	);
};

type AuthStateSignedIn = Omit<Extract<AuthState, { isLoading: false; isSignedIn: true }>, 'isLoading' | 'isSignedIn'>;

const useAuthSignedIn = (): AuthStateSignedIn => {
	const { state } = useContext(AuthContext);

	if (state.isLoading || !state.isSignedIn) {
		throw new Error('`useAuthSignedIn` hook was used outside an AuthGuard!');
	}

	const { token, me } = state;

	return { token, me };
};

export { useAuth, useAuthSignedIn, AuthContext, AuthProvider };
